import { useQuery } from '@tanstack/react-query'
import { PublicProgramQueries } from '@/app/pages/authentication/registration/queries.ts'
import { useArtificialDelay } from '@/common/hooks.ts'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAppAlert } from '@/app/AppAlertProvider.tsx'
import { SyntheticEvent, useEffect, useState } from 'react'
import { EllipsesLoader } from '@/common/components/EllipsesLoader.tsx'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { Label } from '@/common/components/Label.tsx'
import AdvancedSelect from '@/common/components/AdvancedSelect.tsx'
import { PublicProgramsResponse } from '@/queries/programs/requests.ts'
import { ChevronDownIcon } from '@/common/icons/arrows.tsx'
import FormErrorMessage from '@/common/components/FormErrorMessage.tsx'
import { Button } from '@/common/components/Button.tsx'
import { QueryURLKeys } from '@/app/pages/authentication/registration/Page.tsx'
import { useTranslation } from 'react-i18next'
import { getProgramEnrollmentWindowStatus } from '@/apis/programs/requests.ts'

type ProgramSelectorFormProps = {
  renderNextView: () => void
}
type Program = {
  id: number
  name: string
}

export function ProgramSelectorView({
  renderNextView,
}: ProgramSelectorFormProps) {
  const { t: translate } = useTranslation()
  const programQueryResult = useQuery(PublicProgramQueries.index())
  const renderLoader = useArtificialDelay(programQueryResult.isFetching)
  const [enrollmentWindowRequestPending, setEnrollmentWindowRequest] =
    useState(true)
  const navigate = useNavigate()
  const { fireAlert } = useAppAlert()
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [searchParams, setSearchParams] = useSearchParams()
  const handleProgramChange = (program: Program | null) => {
    if (program)
      setSearchParams({ [QueryURLKeys.programID]: program.id.toString() })
    else setSearchParams({})
  }

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault()
    if (searchParams.get(QueryURLKeys.programID) != null) renderNextView()
    else {
      setErrorMessage(translate('please_select_a_program'))
    }
  }

  useEffect(() => {
    getProgramEnrollmentWindowStatus()
      .then((response) => {
        if (response.data?.general_enrollment_open == false) {
          fireAlert({
            variant: 'error',
            message: translate('enrollment_window_closed'),
          }).then(() => {
            navigate(AppRoutePaths.auth.login)
          })
        }
      })
      .catch() // This function will never throw an exception
      .finally(() => {
        setEnrollmentWindowRequest(false)
      })
  }, [])

  if (renderLoader || enrollmentWindowRequestPending) {
    return (
      <div className="flex flex-col justify-center items-center mt-2">
        <EllipsesLoader size="md" />
        <span className="text-sm text-gray-400 mt-1">
          {translate('loading_programs')}
        </span>
      </div>
    )
  }

  if (programQueryResult.isError) {
    fireAlert({
      variant: 'error',
      message: programQueryResult.error.message,
    }).then(() => {
      return navigate(AppRoutePaths.auth.login)
    })
    return null
  }

  return (
    <form className="grid grid-rows-2 space-y-8">
      <div className="space-y-1">
        <Label htmlFor="program" text={translate('program')} required={true} />
        <AdvancedSelect<PublicProgramsResponse>
          options={programQueryResult.data?.data ?? []}
          displayValue={(option) => option?.name ?? ''}
          onChange={handleProgramChange}
          placeholder="Search Programs"
          embeddedIcon={<ChevronDownIcon className="text-lg" />}
          handleFocus={() => setErrorMessage('')}
        />
        <FormErrorMessage message={errorMessage} />
      </div>
      <Button
        type="submit"
        className="justify-self-end max-h-9"
        onClick={handleSubmit}
      >
        {translate('continue')}
      </Button>
    </form>
  )
}
